import { getStandardButtonStyles } from "../../styles/standardButtons";
import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { green, blue } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import { i18n } from "../../translate/i18n";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import {
  Box,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  Switch,
  FormControlLabel
} from "@mui/material";
import {
  Poll as PollIcon,
  Help as HelpIcon,
  Info as InfoIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from "@mui/icons-material";
import { validateVariableName } from "../../utils/variableValidator";

const useStyles = makeStyles(theme => ({
  ...getStandardButtonStyles(theme),
  root: {
    display: "flex",
    flexWrap: "wrap"
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1
  },
  btnWrapper: {
    position: "relative"
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12
  },
  dialogTitle: {
    padding: theme.spacing(2),
    "& h2": {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      "& svg": {
        color: blue[600]
      }
    }
  },
  dialogContent: {
    padding: theme.spacing(2, 3)
  },
  dialogActions: {
    padding: theme.spacing(1, 3, 2)
  },
  chip: {
    margin: theme.spacing(0.5),
    cursor: "pointer",
  },
  infoBox: {
    backgroundColor: "rgba(2, 132, 199, 0.05)",
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    border: `1px solid rgba(2, 132, 199, 0.1)`,
    "& svg": {
      color: blue[600]
    }
  },
  actionButton: {
    borderRadius: 8,
    textTransform: "none",
    padding: theme.spacing(1, 2),
    fontWeight: 500
  },
  optionBox: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1)
  }
}));

const FlowBuilderPollModal = ({ open, onSave, onUpdate, data, close }) => {
  const classes = useStyles();
  const isMounted = useRef(true);
  const [activeModal, setActiveModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState(["", ""]);
  const [multipleSelection, setMultipleSelection] = useState(false);
  const [variableName, setVariableName] = useState("");
  const [variableError, setVariableError] = useState(null);
  const [nodeName, setNodeName] = useState("");
  const questionInputRef = useRef(null);

  const exampleVariables = [
    { name: "nombre", description: i18n.t("flowBuilder.conditionCompare.variables.nombre") },
    { name: "email", description: i18n.t("flowBuilder.conditionCompare.variables.email") }
  ];

  useEffect(() => {
    if (open === "edit") {
      setQuestion(data.data.question || "");
      setOptions(data.data.options && data.data.options.length > 0 ? data.data.options : ["", ""]);
      setMultipleSelection(data.data.multipleSelection || false);
      setVariableName(data.data.variableName || "");
      setNodeName(data.data.name || "");
      setActiveModal(true);
    } else if (open === "create") {
      setQuestion("");
      setOptions(["", ""]);
      setMultipleSelection(false);
      setVariableName("");
      setNodeName("");
      setActiveModal(true);
    } else {
      setActiveModal(false);
    }
  }, [open, data]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
    setLoading(false);
  };

  const handleVariableNameChange = (e) => {
    const value = e.target.value;
    setVariableName(value);
    const validation = validateVariableName(value);
    setVariableError(validation.error);
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
  };

  const handleAddOption = () => {
    if (options.length < 12) {
      setOptions([...options, ""]);
    } else {
      toast.error(i18n.t("flowBuilder.editor.nodes.poll.errorMaxOptions") || "Máximo 12 opciones permitidas en encuestas");
    }
  };

  const handleRemoveOption = (index) => {
    if (options.length > 2) {
      const newOptions = options.filter((_, i) => i !== index);
      setOptions(newOptions);
    } else {
      toast.error(i18n.t("flowBuilder.editor.nodes.poll.errorMinOptions") || "Una encuesta debe tener al menos 2 opciones");
    }
  };

  const handleSave = async () => {
    setLoading(true);

    try {
      // Filtrar opciones vacías
      const validOptions = options.map(o => o.trim()).filter(o => o !== "");
      if (validOptions.length < 2) {
        toast.error(i18n.t("flowBuilder.editor.nodes.poll.errorValidOptions") || "Debes agregar al menos 2 opciones válidas");
        setLoading(false);
        return;
      }
      
      // Limitar a 12
      if (validOptions.length > 12) {
        toast.error(i18n.t("flowBuilder.editor.nodes.poll.errorMaxOptionsLimit") || "Máximo 12 opciones");
        setLoading(false);
        return;
      }

      if (open === "edit") {
        const updateData = {
          ...data,
          data: {
            ...data.data,
            question: question,
            options: validOptions,
            multipleSelection: multipleSelection,
            variableName: variableName,
            name: nodeName
          }
        };
        onUpdate(updateData);
        handleClose();
      } else if (open === "create") {
        const createData = {
          question: question,
          options: validOptions,
          multipleSelection: multipleSelection,
          variableName: variableName,
          name: nodeName
        };
        onSave(createData);
        handleClose();
      }
    } catch (err) {
      console.error(err);
      toastError(err);
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const handleVariableClick = (variable) => {
    if (questionInputRef.current) {
      const inputElement = questionInputRef.current.querySelector('textarea') || questionInputRef.current.querySelector('input');
      if (inputElement) {
        const start = inputElement.selectionStart;
        const end = inputElement.selectionEnd;
        const variableText = `\${${variable.name}}`;
        const newText = question.substring(0, start) + variableText + question.substring(end);
        setQuestion(newText);

        const newCursorPosition = start + variableText.length;
        setTimeout(() => {
          inputElement.focus();
          inputElement.setSelectionRange(newCursorPosition, newCursorPosition);
        }, 100);
      }
    }
  };

  const isFormValid = () => {
    return question.trim() !== "" && variableName.trim() !== "" && options.filter(o => o.trim() !== "").length >= 2 && !variableError;
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={activeModal}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        scroll="paper"
        TransitionProps={{
          style: {
            transition: "all 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
            transform: activeModal ? "none" : "translateY(-20px)",
            opacity: activeModal ? 1 : 0
          }
        }}
      >
        <DialogTitle className={classes.dialogTitle}>
          <Box display="flex" alignItems="center" gap={1}>
            <PollIcon style={{ color: "#0284c7" }} />
            <Typography variant="h6">{open === "edit" ? (i18n.t("flowBuilder.editor.nodes.poll.editTitle") || "Editar Encuesta") : (i18n.t("flowBuilder.editor.nodes.poll.addTitle") || "Añadir Encuesta")}</Typography>
          </Box>
        </DialogTitle>

        <DialogContent className={classes.dialogContent} dividers>
          <Stack spacing={3}>
            <Box>
              <TextField
                label={i18n.t("flowBuilder.input.internalName") || "Nombre Interno"}
                variant="outlined"
                value={nodeName}
                onChange={e => setNodeName(e.target.value)}
                margin="dense"
                fullWidth
                className={classes.textField}
                style={{ width: "100%", marginBottom: "15px" }}
                helperText={i18n.t("flowBuilder.input.internalNameHelper") || "Nombre para identificar este nodo en el editor (ej. 'Encuesta de Satisfacción')"}
              />

              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <Typography variant="subtitle2">{i18n.t("flowBuilder.editor.nodes.poll.question") || "Pregunta o Título de la Encuesta"}</Typography>
              </Stack>

              <TextField
                label={i18n.t("flowBuilder.editor.nodes.poll.question") || "Escribe la pregunta"}
                multiline
                rows={2}
                name="question"
                variant="outlined"
                value={question}
                onChange={e => setQuestion(e.target.value)}
                className={classes.textField}
                fullWidth
                ref={questionInputRef}
                placeholder="Ex: ¿Qué tal fue nuestro servicio?"
              />

              <Box mt={1}>
                <Typography variant="caption" style={{ color: '#aaa', display: 'block', marginBottom: '8px' }}>
                  {i18n.t("flowBuilder.input.availableVariables") || "Variables disponibles (haz clic para insertar):"}
                </Typography>
                <Box display="flex" flexWrap="wrap" mt={0.5}>
                  {exampleVariables.map((variable) => (
                    <Chip
                      key={variable.name}
                      label={variable.name}
                      className={classes.chip}
                      onClick={() => handleVariableClick(variable)}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            </Box>

            <Divider />

            <Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle2">{i18n.t("flowBuilder.editor.nodes.poll.options") || "Opciones"}</Typography>
                <Button 
                  startIcon={<AddIcon />} 
                  size="small" 
                  color="primary"
                  onClick={handleAddOption}
                  disabled={options.length >= 12}
                >
                  {i18n.t("flowBuilder.editor.nodes.poll.addOption") || "Añadir Opción"}
                </Button>
              </Stack>
              
              <Box mb={2}>
                {options.map((option, index) => (
                  <div key={index} className={classes.optionBox}>
                    <TextField
                      label={(i18n.t("flowBuilder.editor.nodes.poll.optionLabel") || "Opción ") + (index + 1)}
                      variant="outlined"
                      size="small"
                      value={option}
                      onChange={(e) => handleOptionChange(index, e.target.value)}
                      fullWidth
                    />
                    <IconButton size="small" onClick={() => handleRemoveOption(index)} disabled={options.length <= 2}>
                      <DeleteIcon fontSize="small" color={options.length <= 2 ? "disabled" : "error"} />
                    </IconButton>
                  </div>
                ))}
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {i18n.t("flowBuilder.editor.nodes.poll.maxOptionsHelper") || "WhatsApp permite un máximo de 12 opciones."}
                </Typography>
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={multipleSelection}
                    onChange={(e) => setMultipleSelection(e.target.checked)}
                    color="primary"
                  />
                }
                label={i18n.t("flowBuilder.editor.nodes.poll.multipleSelection") || "Permitir selección múltiple"}
              />
            </Box>

            <Divider />

            <Box>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <Typography variant="subtitle2">{i18n.t("flowBuilder.editor.nodes.poll.variableLabel") || "Variable para Guardar Respuesta"}</Typography>
                <Tooltip title={i18n.t("flowBuilder.input.variableHelper") || "Nombre de la variable donde se almacenará la respuesta elegida."}>
                  <IconButton size="small" color="primary">
                    <HelpIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>

              <TextField
                label={i18n.t("flowBuilder.input.variableName") || "Nombre de la variable"}
                name="variableName"
                variant="outlined"
                value={variableName}
                onChange={handleVariableNameChange}
                className={classes.textField}
                fullWidth
                required
                error={Boolean(variableError)}
                placeholder="Ex: satisfaccion_cliente"
                helperText={variableError || (i18n.t("flowBuilder.input.variableNameHelper") || "Solo minúsculas, números, guiones bajos (_) y guiones (-)")}
              />
            </Box>

            <Box className={classes.infoBox}>
              <Stack direction="row" spacing={1}>
                <InfoIcon fontSize="small" />
                <Box>
                  <Typography variant="subtitle2">{i18n.t("flowBuilder.input.howItWorks") || "Cómo funciona"}</Typography>
                  <Typography variant="body2">{i18n.t("flowBuilder.editor.nodes.poll.howItWorksDesc") || "Esta encuesta se enviará nativamente en dispositivos compatibles (como WhatsApp). Al votar, la opción elegida se guardará en la variable y el flujo continuará al siguiente nodo de forma automática."}</Typography>
                </Box>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <Button
            onClick={handleClose}
            className={classes.secondaryButton}
          >
            {i18n.t("contactModal.buttons.cancel") || "Cancelar"}
          </Button>

          <div className={classes.btnWrapper}>
            <Button
              type="submit"
              onClick={handleSave}
              disabled={loading || !isFormValid()}
              className={classes.applyButton}
            >
              {open === "edit" ? (i18n.t("common.save") || "Guardar cambios") : (i18n.t("common.add") || "Añadir")}
            </Button>
            {loading && (
              <CircularProgress size={24} className={classes.buttonProgress} />
            )}
          </div>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FlowBuilderPollModal;
